import { useEffect, useRef, type RefObject } from "react";

const FOKUSIERBAR = "input:not(:disabled), select:not(:disabled), button:not(:disabled)";

export interface FokusfalleOptionen {
  dialogRef: RefObject<HTMLElement | null>;
  /** Waehrend `true` (Speichern laeuft) schliesst Escape den Dialog nicht. */
  beschaeftigtRef: RefObject<boolean>;
  onSchliessen: () => void;
  /** Das Element, das beim Oeffnen den Fokus bekommt; ohne Angabe das erste Eingabe-/Auswahlfeld. */
  ersterFokus?: () => HTMLElement | null | undefined;
}

/**
 * Fokusfalle und Escape fuer die modalen Dialoge der Zahlungsseite (Tab/Shift+Tab kreisen im Dialog,
 * Escape schliesst, solange nichts gespeichert wird). Eine Implementierung fuer `SicherungAnlegenDialog`,
 * `SicherungLoeschenDialog` und `FxDialog` (vorher drei Kopien).
 */
export function useFokusfalle({ dialogRef, beschaeftigtRef, onSchliessen, ersterFokus }: FokusfalleOptionen): void {
  // Der Schliessen-Rueckruf wechselt bei jedem Render der Eltern (z. B. nach jedem Speichern im Detail-Panel); der Effekt
  // darf deshalb nicht daran haengen, sonst springt der Fokus bei jeder Eingabe zurueck auf das erste Feld.
  const onSchliessenRef = useRef(onSchliessen);
  onSchliessenRef.current = onSchliessen;
  useEffect(() => {
    (ersterFokus?.() ?? dialogRef.current?.querySelector<HTMLElement>("input:not(:disabled), select:not(:disabled)"))?.focus();
    const fokusHalten = (ereignis: KeyboardEvent) => {
      if (ereignis.key === "Escape") {
        if (!beschaeftigtRef.current) onSchliessenRef.current();
        return;
      }
      if (ereignis.key !== "Tab") return;
      const fokusierbar = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOKUSIERBAR) ?? []);
      if (!fokusierbar.length) {
        ereignis.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const erstes = fokusierbar[0];
      const letztes = fokusierbar.at(-1)!;
      if (document.activeElement === dialogRef.current) {
        ereignis.preventDefault();
        (ereignis.shiftKey ? letztes : erstes).focus();
      } else if (ereignis.shiftKey && (document.activeElement === erstes || !dialogRef.current?.contains(document.activeElement))) {
        ereignis.preventDefault();
        letztes.focus();
      } else if (!ereignis.shiftKey && document.activeElement === letztes) {
        ereignis.preventDefault();
        erstes.focus();
      }
    };
    document.addEventListener("keydown", fokusHalten);
    return () => document.removeEventListener("keydown", fokusHalten);
    // Nur beim Oeffnen: `ersterFokus` und der Fokus-Sprung laufen genau einmal, der Rueckruf kommt ueber die Ref.
  }, []);
}
